import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Controller, useForm } from 'react-hook-form';
import { Typeahead } from 'react-bootstrap-typeahead';
import PhoneInput from 'react-phone-number-input';
import { useTranslation } from 'react-i18next';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-phone-number-input/style.css';
import './form.css';

const eventUrl = 'https://superstudent.niecindonesia.com/event/';
const baseUrl = 'https://superstudent.niecindonesia.com/api/v1/application/';
const eventToken = 'eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ';

const Form = () => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolCode, setSelectedSchoolCode] = useState(null);
  const [schoolMissing, setSchoolMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const provinceId = watch('province_id');

  useEffect(() => {
    axios.get(`${baseUrl}master/provinces`)
      .then((response) => setProvinces(response.data.data || []))
      .catch(() => setProvinces([]));
  }, []);

  useEffect(() => {
    setCities([]);
    setSchools([]);
    setSelectedSchoolCode(null);

    if (!provinceId) return;

    axios.get(`${baseUrl}master/kabupaten/${provinceId}`)
      .then((response) => setCities(response.data.data || []))
      .catch(() => setCities([]));

    axios.get(`${baseUrl}sekolah-indonesia?province_id=${provinceId}&code_jenjang=JP002`)
      .then((response) => setSchools(response.data.data || []))
      .catch(() => setSchools([]));
  }, [provinceId]);

  const handleSchool = (selection) => {
    setSelectedSchoolCode(selection.length > 0 ? selection[0].kode_sekolah : null);
  };

  const handleMissingSchool = (event) => {
    const checked = event.target.checked;
    setSchoolMissing(checked);
    if (checked) setSelectedSchoolCode(null);
  };

  const onSubmit = async (formData) => {
    setLoading(true);

    const payload = {
      ...formData,
      sekolah: selectedSchoolCode,
      id_konten: '511',
      code_jenjang: 'JP002',
      phone: formData.phone.replace('+', ''),
      keterangan: `${formData.usia} tahun, ${formData.nama_sekolah || ''}`,
    };

    try {
      await axios.post(`${eventUrl}register`, payload, {
        headers: { Authorization: `Bearer ${eventToken}` },
      });

      reset();
      setSelectedSchoolCode(null);
      setSchoolMissing(false);
      await Swal.fire({
        title: t('form.success_title'),
        text: t('form.success_message'),
        icon: 'success',
        confirmButtonText: t('form.success_button'),
      });
    } catch (error) {
      await Swal.fire({
        title: t('form.error_title'),
        text: t('form.error_message'),
        icon: 'error',
        confirmButtonText: t('form.success_button'),
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (name) => (
    errors[name]?.message
      ? <span className="field-error" role="alert">{errors[name].message}</span>
      : null
  );

  return (
    <aside className="registration-card" aria-labelledby="registration-title">
      <div className="registration-card__header">
        <h2 id="registration-title">{t('form.title')}</h2>
        <p>{t('form.subtitle')}</p>
      </div>

      <form className="registration-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-field">
          <label htmlFor="name">{t('form.label_name')}</label>
          <input
            {...register('name', { required: t('form.validate_name') })}
            id="name"
            type="text"
            className={errors.name ? 'is-invalid' : ''}
            placeholder={t('form.placeholer_name')}
            autoComplete="name"
          />
          {fieldError('name')}
        </div>

        <div className="form-field">
          <label htmlFor="email">{t('form.label_email')}</label>
          <input
            {...register('email', {
              required: t('form.validate_email'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                message: t('form.invalid_email'),
              },
            })}
            id="email"
            type="email"
            className={errors.email ? 'is-invalid' : ''}
            placeholder={t('form.placeholder_email')}
            autoComplete="email"
          />
          {fieldError('email')}
        </div>

        <div className="form-field">
          <label htmlFor="phone">{t('form.label_phone')}</label>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t('form.validate_phone'),
              validate: (value) => (value && value.length >= 10) || t('form.invalid_phone'),
            }}
            render={({ field }) => (
              <PhoneInput
                {...field}
                id="phone"
                defaultCountry="ID"
                international
                countryCallingCodeEditable={false}
                className={errors.phone ? 'phone-field is-invalid' : 'phone-field'}
                placeholder={t('form.placeholer_phone')}
              />
            )}
          />
          {fieldError('phone')}
        </div>

        <div className="form-field">
          <label htmlFor="age">{t('form.label_age')}</label>
          <input
            {...register('usia', { required: t('form.validate_old') })}
            id="age"
            type="number"
            min="13"
            max="18"
            className={errors.usia ? 'is-invalid' : ''}
            placeholder={t('form.placeholer_old')}
          />
          {fieldError('usia')}
        </div>

        <div className="form-field">
          <label htmlFor="province_id">{t('form.label_province')}</label>
          <select
            {...register('province_id', { required: t('form.validate_province') })}
            id="province_id"
            className={errors.province_id ? 'is-invalid' : ''}
          >
            <option value="">{t('form.placeholer_province')}</option>
            {provinces.map((item) => (
              <option key={item.id} value={item.id}>{item.province_name}</option>
            ))}
          </select>
          {fieldError('province_id')}
        </div>

        <div className="form-field">
          <label htmlFor="kabupaten_id">{t('form.label_city')}</label>
          <select
            {...register('kabupaten_id', { required: t('form.validate_kabupaten') })}
            id="kabupaten_id"
            className={errors.kabupaten_id ? 'is-invalid' : ''}
            disabled={!provinceId}
          >
            <option value="">{t('form.placeholer_kabupaten')}</option>
            {cities.map((item) => (
              <option key={item.id} value={item.id}>{item.kabupaten_name}</option>
            ))}
          </select>
          {fieldError('kabupaten_id')}
        </div>

        <div className="form-field">
          <label htmlFor="school-typeahead">{t('form.label_school')}</label>
          <Typeahead
            id="school-typeahead"
            placeholder={t('form.placeholer_school')}
            onChange={handleSchool}
            labelKey="nama_sekolah"
            options={schools}
            disabled={!provinceId || schoolMissing}
            inputProps={{ 'aria-label': t('form.label_school') }}
          />
        </div>

        <label className="school-missing">
          <input
            {...register('status_sekolah')}
            type="checkbox"
            checked={schoolMissing}
            onChange={handleMissingSchool}
          />
          <span>{t('form.placeholer_school_ask')}</span>
        </label>

        {schoolMissing && (
          <div className="form-field">
            <label htmlFor="school-manual">{t('form.label_school_manual')}</label>
            <input
              {...register('nama_sekolah')}
              id="school-manual"
              type="text"
              placeholder={t('form.placeholer_inpu_school')}
            />
          </div>
        )}

        <button className="registration-form__submit" type="submit" disabled={loading}>
          {loading ? t('form.button_registering') : t('form.button_register')}
        </button>

        <p className="registration-form__privacy">
          <i className="fa-solid fa-lock" aria-hidden="true" />
          {t('form.privacy')}
        </p>
      </form>
    </aside>
  );
};

export default Form;
